/**
 * Dataset Driver Sets Editor
 *
 * Edits a page's per-dataset driver sets: one tab per configured dataset, a trailing "+ Add Source" tab,
 * and a delete link under each set. "+ Add Source" offers the datasets that are enabled for the deck,
 * in `allowedDatasets`, and not yet set up on this page; it's hidden when there are none. Used by the wizard's Page Driver step and its % denominator step; the caller owns the state and
 * the rules (defaults, delete guard).
 */

import './PageDriverStep.scss';

import { KPIDriversDisplay } from '@deckManagement/components/Common/KPIDriversDisplay';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import { generateDriverDisplayOptions } from '@utils/kpiDriverHelpers';
import { isNil } from 'lodash';
import { Dispatch, MouseEvent, SetStateAction, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { AnyKPIDrivers, KpiDimension, KpiDriverValues } from 'types/PageSidePanel.types';

export type DatasetDriverSets = Partial<Record<DatasetSource, AnyKPIDrivers>>;

export interface DatasetDriverSetsEditorProps {
  /** The driver set per configured dataset. */
  driverSets: DatasetDriverSets;
  /** State setter for `driverSets`. */
  onChange: Dispatch<SetStateAction<DatasetDriverSets>>;
  /** Datasets the deck permits; orders the tabs and bounds "+ Add Source". */
  enabledDatasets: DatasetSource[];
  /** Datasets this editor may edit and add. Omitted = every dataset. */
  allowedDatasets?: DatasetSource[];
  /**
   * Datasets to show as a tab even without a set (e.g. the page's datasets). A tab for a dataset that
   * is not allowed shows `notAllowedMessage` instead of fields.
   */
  shownDatasets?: DatasetSource[];
  /** The set a dataset starts with when added. */
  defaultsFor: (dataset: DatasetSource) => AnyKPIDrivers;
  /** Whether a dataset's delete link is shown, given the configured datasets. */
  canDelete?: (dataset: DatasetSource, configuredDatasets: DatasetSource[]) => boolean;
  /** A reason the dataset cannot be deleted right now, or null to allow it. */
  deleteGuard?: (dataset: DatasetSource) => string | null;
  /** Called with the guard's reason when a delete is blocked. */
  onDeleteBlocked?: (reason: string) => void;
  /** Text of the delete link. */
  deleteLabel: string;
  /** Text shown in place of fields for a dataset that is not allowed. */
  notAllowedMessage?: (dataset: DatasetSource) => string;
  /** Prefix for the tab and panel ids, unique per editor on a screen. */
  idPrefix?: string;
}

const baseClassName = 'page-driver-step';
// Sentinel Tab value for the "+ Add Source" affordance — never a real selected dataset.
const ADD_SOURCE_TAB = '__add_source__';

const defaultNotAllowedMessage = (dataset: DatasetSource) =>
  `${resolveDatasetSourceLabel(dataset)} is not enabled here yet`;

const DatasetDriverSetsEditor = ({
  driverSets,
  onChange,
  enabledDatasets,
  allowedDatasets,
  shownDatasets = [],
  defaultsFor,
  canDelete = () => true,
  deleteGuard,
  onDeleteBlocked,
  deleteLabel,
  notAllowedMessage = defaultNotAllowedMessage,
  idPrefix = 'kpidrivers',
}: DatasetDriverSetsEditorProps) => {
  const isAllowed = useCallback(
    (dataset: DatasetSource) => isNil(allowedDatasets) || allowedDatasets.includes(dataset),
    [allowedDatasets],
  );
  const [activeDataset, setActiveDataset] = useState<DatasetSource | null>(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);

  /** Datasets with an allowed set, ordered by the deck's enabled order (any extras appended). */
  const configuredDatasets = useMemo<DatasetSource[]>(() => {
    const present = (Object.keys(driverSets) as DatasetSource[]).filter(
      (ds) => !isNil(driverSets[ds]) && isAllowed(ds),
    );
    const ordered = enabledDatasets.filter((ds) => present.includes(ds));
    const extras = present.filter((ds) => !enabledDatasets.includes(ds));
    return [...ordered, ...extras];
  }, [isAllowed, enabledDatasets, driverSets]);

  /** Shown datasets that are not allowed here; they render a message instead of fields. */
  const messageDatasets = useMemo<DatasetSource[]>(
    () => shownDatasets.filter((ds) => !isAllowed(ds)),
    [isAllowed, shownDatasets],
  );

  /** Every tab, in the deck's enabled order. */
  const tabDatasets = useMemo<DatasetSource[]>(() => {
    const all = [...configuredDatasets, ...messageDatasets];
    const ordered = enabledDatasets.filter((ds) => all.includes(ds));
    return [...ordered, ...all.filter((ds) => !enabledDatasets.includes(ds))];
  }, [configuredDatasets, enabledDatasets, messageDatasets]);

  /** Enabled, allowed datasets not yet configured — choices for the "+ Add Source" menu. */
  const addableDatasets = useMemo<DatasetSource[]>(
    () => enabledDatasets.filter((ds) => isAllowed(ds) && isNil(driverSets[ds])),
    [isAllowed, enabledDatasets, driverSets],
  );

  // Fall back to the first tab when the active dataset was removed (or nothing was chosen yet).
  const currentDataset =
    !isNil(activeDataset) && tabDatasets.includes(activeDataset) ? activeDataset : (tabDatasets[0] ?? false);

  /**
   * DriverDisplayOptionMap per configured dataset. Every configured set keeps its own KPIDriversDisplay
   * mounted (see the panels below) so switching tabs never remounts it and re-fires its network calls.
   */
  const driverOptionsByDataset = useMemo(
    () =>
      configuredDatasets.reduce(
        (acc, dataset) => {
          acc[dataset] = generateDriverDisplayOptions(driverSets[dataset] ?? defaultsFor(dataset));
          return acc;
        },
        {} as Record<DatasetSource, ReturnType<typeof generateDriverDisplayOptions>>,
      ),
    [configuredDatasets, defaultsFor, driverSets],
  );

  /** Builds a change handler that routes KPIDriversDisplay edits to the given dataset's set. */
  const handleChangeFor = useCallback(
    (dataset: DatasetSource) => (_key: KpiDimension, updates: KpiDriverValues) => {
      onChange((prev) => ({
        ...prev,
        [dataset]: { ...(prev[dataset] ?? defaultsFor(dataset)), ...updates },
      }));
    },
    [defaultsFor, onChange],
  );

  /** Switches tabs; the "+ Add Source" sentinel does not change the selected dataset */
  const handleTabChange = useCallback((_event: SyntheticEvent, value: string) => {
    if (value !== ADD_SOURCE_TAB) {
      setActiveDataset(value as DatasetSource);
    }
  }, []);

  const openAddMenu = useCallback((event: MouseEvent<HTMLElement>) => setAddMenuAnchor(event.currentTarget), []);
  const closeAddMenu = useCallback(() => setAddMenuAnchor(null), []);

  /** Adds the chosen dataset (seeded with its defaults) and switches focus to it */
  const handleAddSource = useCallback(
    (dataset: DatasetSource) => {
      onChange((prev) => (prev[dataset] ? prev : { ...prev, [dataset]: defaultsFor(dataset) }));
      setActiveDataset(dataset);
      closeAddMenu();
    },
    [closeAddMenu, defaultsFor, onChange],
  );

  /** Removes the dataset's set unless the guard blocks it; focus moves to the first remaining tab. */
  const handleDeleteSource = useCallback(
    (dataset: DatasetSource) => {
      if (!canDelete(dataset, configuredDatasets)) return;
      const reason = deleteGuard?.(dataset) ?? null;
      if (reason) {
        onDeleteBlocked?.(reason);
        return;
      }
      onChange((prev) => {
        const next = { ...prev };
        delete next[dataset];
        return next;
      });
      setActiveDataset((prev) => (prev === dataset ? null : prev));
    },
    [canDelete, configuredDatasets, deleteGuard, onChange, onDeleteBlocked],
  );

  return (
    <Box className={baseClassName}>
      <Tabs value={currentDataset} onChange={handleTabChange} className={`${baseClassName}__tabs`}>
        {tabDatasets.map((dataset) => (
          <Tab
            className={`${baseClassName}__tab-item`}
            key={dataset}
            value={dataset}
            label={resolveDatasetSourceLabel(dataset)}
            id={`${idPrefix}-tab-${dataset}`}
            aria-controls={`${idPrefix}-panel-${dataset}`}
          />
        ))}
        {addableDatasets.length > 0 && (
          // Label-only (no `icon`): a Tab with an icon takes MUI's taller `labelIcon` variant, which
          // would stretch the whole tab row's height.
          <Tab
            className={`${baseClassName}__tab-item`}
            key={ADD_SOURCE_TAB}
            value={ADD_SOURCE_TAB}
            label='+ Add Source'
            onClick={openAddMenu}
          />
        )}
      </Tabs>

      {configuredDatasets.map((dataset) => (
        // Every configured set stays mounted; `hidden` (not conditional rendering) toggles which is
        // shown, so tab switches never unmount KPIDriversDisplay and re-trigger its network calls.
        <Box
          key={`${idPrefix}-panel-${dataset}`}
          id={`${idPrefix}-panel-${dataset}`}
          role='tabpanel'
          hidden={dataset !== currentDataset}
          aria-labelledby={`${idPrefix}-tab-${dataset}`}
        >
          <KPIDriversDisplay
            className={`${baseClassName}__stack`}
            datasetSource={dataset}
            driverOptions={driverOptionsByDataset[dataset]}
            renderMode='dimensionSelect'
            hideSwitches
            excludeDimensions={[KpiDimension.REPORTING_CURRENCY]}
            onChange={handleChangeFor(dataset)}
          />
          {canDelete(dataset, configuredDatasets) && (
            <Box className={`${baseClassName}__delete-row`}>
              <button
                type='button'
                className={`${baseClassName}__delete-source`}
                onClick={() => handleDeleteSource(dataset)}
              >
                {deleteLabel}
              </button>
            </Box>
          )}
        </Box>
      ))}

      {messageDatasets.map((dataset) => (
        <Box
          key={`${idPrefix}-panel-${dataset}`}
          id={`${idPrefix}-panel-${dataset}`}
          role='tabpanel'
          hidden={dataset !== currentDataset}
          aria-labelledby={`${idPrefix}-tab-${dataset}`}
        >
          <Alert severity='info' className={`${baseClassName}__stack`}>
            {notAllowedMessage(dataset)}
          </Alert>
        </Box>
      ))}

      <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={closeAddMenu}>
        {addableDatasets.map((dataset) => (
          <MenuItem key={dataset} onClick={() => handleAddSource(dataset)}>
            {resolveDatasetSourceLabel(dataset)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default DatasetDriverSetsEditor;
